import * as Dialog from "@radix-ui/react-dialog"
import { router } from "@inertiajs/react"
import { Loader2, X } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { Button } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { activateShotPromptVersion, fetchShotPromptVersions, type ShotPromptVersionRow } from "@/lib/workspace-api"
import { cn } from "@/lib/utils"

type Props = {
  projectId: number
  shotId: number
  kind: "storyboard" | "video"
  open: boolean
  onOpenChange: (open: boolean) => void
  onPick: (systemPrompt: string, userPrompt: string) => void
  onDataChanged?: () => void
}

export function PromptVersionsDialog({ projectId, shotId, kind, open, onOpenChange, onPick, onDataChanged }: Props) {
  const { t, i18n } = useTranslation(["shot", "common"])
  const [versions, setVersions] = useState<ShotPromptVersionRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activatingId, setActivatingId] = useState<number | null>(null)

  const sourceLabel = (source: string) => {
    const key = `promptVersions.source.${source}` as "promptVersions.source.manual"
    const translated = t(key, { defaultValue: source })
    return translated
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await fetchShotPromptVersions(projectId, shotId, kind)
      setVersions(rows)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setVersions([])
    } finally {
      setLoading(false)
    }
  }, [projectId, shotId, kind])

  useEffect(() => {
    if (open) void load()
  }, [open, load])

  const handleActivate = async (v: ShotPromptVersionRow) => {
    setActivatingId(v.id)
    setError(null)
    try {
      await activateShotPromptVersion(projectId, shotId, v.id)
      onDataChanged?.()
      router.reload()
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setActivatingId(null)
    }
  }

  const title = kind === "storyboard" ? t("promptVersions.titleStoryboard") : t("promptVersions.titleVideo")
  const locale = i18n.language?.startsWith("ru") ? "ru-RU" : "en-US"

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-dialog-overlay fixed inset-0 z-[110] bg-[var(--ui-overlay-heavy)] backdrop-blur-sm" />
        <Dialog.Content className="ui-dialog-content fixed top-1/2 left-1/2 z-[111] flex max-h-[85vh] w-[min(520px,92vw)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-ui-border shadow-2xl shadow-black/50 focus:outline-none dark:bg-zinc-950 light:bg-ui-bg-elevated">
          <div className="flex items-center justify-between border-b border-ui-border px-4 py-3">
            <Dialog.Title className="text-sm font-semibold text-ui-text-primary">{title}</Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-lg p-1 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
                aria-label={t("common.close")}
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <div className="it2tv-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-3">
            {loading ? (
              <p className="flex items-center gap-2 text-sm text-ui-text-secondary">
                <Loader2 className="h-4 w-4 animate-spin" /> {t("promptVersions.loading")}
              </p>
            ) : null}
            {error ? <p className="text-sm text-ui-status-failed">{error}</p> : null}
            {!loading && !error && versions.length === 0 ? (
              <p className="text-sm text-ui-text-muted">{t("promptVersions.empty")}</p>
            ) : null}
            <ul className="space-y-3">
              {versions.map((v) => (
                <li
                  key={v.id}
                  className={cn(
                    "rounded-lg border p-3",
                    v.active
                      ? "border-ui-accent/60 dark:bg-violet-950/20 light:border-ui-border-strong light:bg-clay-2 light:shadow-[var(--shadow-clay-inset)]"
                      : "cursor-pointer border-ui-border bg-ui-surface-sunken",
                  )}
                  onDoubleClick={() => {
                    if (!v.active && activatingId === null) void handleActivate(v)
                  }}
                  title={!v.active ? t("promptVersions.doubleClickActivate") : undefined}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] text-ui-text-muted tabular-nums">
                      {new Date(v.created_at).toLocaleString(locale, {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    <span className="rounded bg-ui-surface-raised px-1.5 py-0.5 text-[9px] tracking-wide text-ui-text-secondary uppercase">
                      {sourceLabel(v.source)}
                    </span>
                    {v.active ? (
                      <span className="rounded bg-ui-accent/80 px-1.5 py-0.5 text-[9px] font-semibold text-ui-on-accent uppercase">
                        {t("promptVersions.active")}
                      </span>
                    ) : activatingId === v.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-ui-text-muted" />
                    ) : null}
                  </div>
                  <p className="mt-2 line-clamp-3 font-mono text-[11px] leading-relaxed text-ui-text-secondary">
                    {v.user_prompt || "—"}
                  </p>
                  {v.used_in_media.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {v.used_in_media
                        .slice(0, 4)
                        .map((m) =>
                          m.preview_thumb_url || m.preview_url ? (
                            <img
                              key={m.id}
                              src={m.preview_thumb_url ?? m.preview_url ?? ""}
                              alt=""
                              className="h-8 w-8 rounded border border-ui-border object-cover"
                            />
                          ) : null,
                        )}
                    </div>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="h-7 text-[11px]"
                      disabled={activatingId !== null}
                      onClick={(e) => {
                        e.stopPropagation()
                        onPick(v.system_prompt, v.user_prompt)
                        onOpenChange(false)
                      }}
                    >
                      {t("promptVersions.insertIntoField")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
